import { useState } from "react";
import { logError } from "@entrilia/shared/logging";
import { validResult } from "@entrilia/shared/utilities/validators";
import type { ObjectFieldValues } from "../../../api/types/objectTypes";
import type { EntityFieldComponentProps, EntityFieldComponentPropsBase } from "../entityFieldTypes";
import { useFieldValuesContext } from "../FieldValuesContext";
import { getInvalidFieldValueError, getLookupOptionDisplayValue, getSelectOptionsForLookup } from "../helpers";
import NewObjectDialog from "../NewObjectDialog";
import ObjectPageLink from "../ObjectPageLink";
import EntityFieldSelectComponent from "./EntityFieldSelectComponent";

interface EntityLookupFieldComponentProps extends EntityFieldComponentProps<string> {
  targetObjectType: string;
  onCreateOption?: () => void;
}

const EntityLookupFieldComponent = ({
  targetObjectType,
  value,
  fieldId,
  fieldAttributes,
  onChange,
  fieldInputId,
  onCreateOption,
}: EntityLookupFieldComponentProps) => {
  const { lookupObjects, lookupOptionsByObjectType } = useFieldValuesContext();

  const availableLookupReferences = lookupOptionsByObjectType[targetObjectType] ?? [];
  const objectLookupReferences = lookupObjects[fieldId] ?? [];
  const allLookupReferences = [...availableLookupReferences, ...objectLookupReferences];

  const options = getSelectOptionsForLookup(availableLookupReferences);
  const selectedOptionLabel = getLookupOptionDisplayValue(value, allLookupReferences);
  const entityTypeCode = allLookupReferences.find((ref) => ref.objectId === value)?.entityType;

  return (
    <EntityFieldSelectComponent
      fieldId={fieldId}
      value={value}
      options={options}
      fieldAttributes={fieldAttributes}
      onChange={(value, validationResult) => onChange(value, fieldId, validationResult)}
      onCreateOption={onCreateOption}
      fieldInputId={fieldInputId}
      displayValueComponent={
        value && selectedOptionLabel ? (
          <ObjectPageLink objectType={targetObjectType} objectId={value} entityTypeCode={entityTypeCode}>
            {selectedOptionLabel}
          </ObjectPageLink>
        ) : null
      }
    />
  );
};

const EntityLookupField = ({
  value,
  fieldId,
  fieldConfiguration,
  fieldAttributes,
  onChange,
  fieldInputId,
}: EntityFieldComponentPropsBase) => {
  const { addLookupOption } = useFieldValuesContext();

  const [newObjectDialogOpen, setNewObjectDialogOpen] = useState(false);

  if (typeof value !== "string" && value !== undefined && value !== null) {
    logError(getInvalidFieldValueError(fieldId, fieldConfiguration?.$type, value), "[EntityLookupField]");
    return null;
  }

  if (fieldConfiguration?.$type !== "Lookup") {
    const error = `Invalid configuration for Lookup field: ${fieldConfiguration?.$type}`;
    logError(error, "EntityLookupField");
    return null;
  }

  const handleCreateOption = () => {
    setNewObjectDialogOpen(true);
  };

  const handleNewObjectCreated = (newObject: ObjectFieldValues) => {
    setNewObjectDialogOpen(false);

    onChange(newObject.id, fieldId, validResult());

    addLookupOption(fieldConfiguration.objectType, {
      objectType: fieldConfiguration.objectType,
      entityType: fieldConfiguration.newObjectEntityType,
      objectId: newObject.id,
      primaryFieldValue: newObject.displayValues.primaryValue,
      secondaryFieldValue: newObject.displayValues.secondaryValue,
    });
  };

  return (
    <>
      <EntityLookupFieldComponent
        targetObjectType={fieldConfiguration.objectType}
        value={value}
        fieldId={fieldId}
        fieldConfiguration={fieldConfiguration}
        fieldAttributes={fieldAttributes}
        onChange={onChange}
        fieldInputId={fieldInputId}
        onCreateOption={fieldConfiguration.allowCreateNewObjects ? handleCreateOption : undefined}
      />

      {newObjectDialogOpen && (
        <NewObjectDialog
          objectType={fieldConfiguration.objectType}
          entityTypeCode={fieldConfiguration.newObjectEntityType}
          onClose={() => setNewObjectDialogOpen(false)}
          onCreated={handleNewObjectCreated}
        />
      )}
    </>
  );
};

export default EntityLookupField;
